import { DateTime } from "luxon";
import { FREEFORM_REASON_CHAR_LIMIT_FOR_BID, NON_CONTRACT, FREEFORM_REASON_CHAR_LIMIT } from "../constants";
import { hasSpecialCharacters } from "../commonUtils";
import { ONE_YEAR_FROM, THREE_YEARS_IN_PAST } from "../dateHelper";
import {
  CHAR_LIMIT_MESSAGE,
  COMMENTS_REQUIRED,
  END_DATE_IS_REQUIRED,
  INVALID_EMPLOYEE_ID,
  LABOR_CONTACT_CHAR_LIMIT,
  LABOR_CONTACT_REQUIRED,
  START_DATE_IS_REQUIRED,
} from "../messageConstants";
import { getTimeZoneFromSwaStation } from "../timeZoneService";
import { isOpenBid, isPendingBid } from "./bidCommon";
import { getFirstDayWithMonthOffset } from "./timeOffBidUtils";

export function validateBidStartDateFilter(input, endDateFilter, station) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  if (endDateFilter.toMillis() < input.toMillis()) {
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() < THREE_YEARS_IN_PAST(station).toMillis()) {
    return "Start Date cannot be prior to 3 years in the past";
  }
  if (endDateFilter.toMillis() > ONE_YEAR_FROM(station, input).toMillis()) {
    return "Start Date cannot be over 1 year before End Date";
  }

  return "";
}

export function validateBidEndDateFilter(input, startDateFilter, station) {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }

  if (input.toMillis() < startDateFilter.toMillis()) {
    // end date is before start date
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() > ONE_YEAR_FROM(station, startDateFilter).toMillis()) {
    return "End Date cannot be over 1 year after Start Date";
  }
  return "";
}

export function validateBidType(bidType) {
  if (bidType.trim() === "") {
    return "Bid Type is required";
  }
  return "";
}

export function validateWorkgroup(workgroup) {
  if (workgroup.trim() === "") {
    return "Workgroup is required";
  }
  return "";
}

export function validateBidReasonComments(input, isRequired) {
  const trimmedInput = input.trim();
  if (isRequired && trimmedInput === "") {
    return COMMENTS_REQUIRED;
  }
  if (trimmedInput.length > FREEFORM_REASON_CHAR_LIMIT_FOR_BID) {
    return CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT_FOR_BID);
  }
  return "";
}

export function validateLaborContactTextInput(input) {
  const trimmedInput = input.trim();
  if (trimmedInput === "") {
    return LABOR_CONTACT_REQUIRED;
  }
  if (trimmedInput.length > FREEFORM_REASON_CHAR_LIMIT) {
    return LABOR_CONTACT_CHAR_LIMIT;
  }
  return "";
}

export function validateBidSchedStartDate(schedDates) {
  if (!schedDates[0] && schedDates[1]) {
    return "Bid Effective Start Date is required";
  }
  return "";
}

export function validateBidSchedEndDate(schedDates) {
  if (schedDates[0] && !schedDates[1]) {
    return "Bid Effective End Date is required";
  }
  return "";
}

export function validateBidSchedDates(schedDates, windowDates) {
  if (schedDates[0] && schedDates[1]) {
    const daysDiff = schedDates[1].diff(schedDates[0], ["days"]).values.days;
    if (daysDiff < 27) {
      return "Shift Bid period cannot be less than 28 days";
    }

    // do validation with window dates as well
    if (
      windowDates[0] &&
      windowDates[1] &&
      (schedDates[0].toMillis() <= windowDates[0].toMillis() || schedDates[0].toMillis() <= windowDates[1].toMillis())
    ) {
      return "Bid Effective Dates must be later than the Bid Window Dates";
    }
  }
  return "";
}

export function validateBidEffectiveDates(schedMonth, schedYear) {
  if (schedMonth.trim() === "" || schedYear.trim() === "") {
    return "Bid Effective Month and Year are required";
  }

  return "";
}

export function validateFreedayBidWindowDates(windowDates, schedMonth, schedYear, bidStatus, station) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    const effectiveDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
    const firstDayOfGivenMonth = effectiveDate
      .minus({ months: process.env.REACT_APP_FREEDAY_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH })
      .startOf("month");
    const lastDayOfPrevMonth = effectiveDate.minus({ months: 1 }).endOf("month");
    if (
      schedMonth &&
      schedYear &&
      (windowDates[0].toMillis() < firstDayOfGivenMonth.toMillis() ||
        windowDates[1].toMillis() > lastDayOfPrevMonth.toMillis())
    ) {
      return `Bid Open and Close Dates must be within ${process.env.REACT_APP_FREEDAY_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH} months of the Bid Effective month`;
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }
  return "";
}

export function validateIAMFTOBidWindowDates(windowDates, schedMonth, schedYear, bidStatus, station) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    const effectiveDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
    const firstDayOfGivenMonth = getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_IAM_FTO_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    );

    const lastDayOfPrevMonth = effectiveDate.minus({ months: 1 }).endOf("month");
    if (
      schedMonth &&
      schedYear &&
      (windowDates[0].toMillis() < firstDayOfGivenMonth.toMillis() ||
        windowDates[1].toMillis() > lastDayOfPrevMonth.toMillis())
    ) {
      return `Bid Open and Close Dates must be within ${process.env.REACT_APP_IAM_DAT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH} months of the Bid Effective month`;
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }

  return "";
}

export function validateIAMDATBidWindowDates(windowDates, schedMonth, schedYear, bidStatus) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    const effectiveDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
    const firstDayOfGivenMonth = getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_IAM_DAT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    );

    const lastDayOfPrevMonth = effectiveDate.minus({ months: 1 }).endOf("month");
    if (
      schedMonth &&
      schedYear &&
      (windowDates[0].toMillis() < firstDayOfGivenMonth.toMillis() ||
        windowDates[1].toMillis() > lastDayOfPrevMonth.toMillis())
    ) {
      return `Bid Open and Close Dates must be within ${process.env.REACT_APP_IAM_FTO_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH} months of the Bid Effective month`;
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }

  return "";
}

export function validateTWUDATFTOBidWindowDates(windowDates, schedMonth, schedYear, bidStatus) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    const effectiveDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
    const firstDayOfGivenMonth = getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_TWU_DAT_FTO_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    );

    const lastDayOfPrevMonth = effectiveDate.minus({ months: 1 }).endOf("month");
    if (
      schedMonth &&
      schedYear &&
      (windowDates[0].toMillis() < firstDayOfGivenMonth.toMillis() ||
        windowDates[1].toMillis() > lastDayOfPrevMonth.toMillis())
    ) {
      return `Bid Open and Close Dates must be within ${process.env.REACT_APP_TWU_DAT_FTO_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH} months of the Bid Effective month`;
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }

  return "";
}

export function validateBidWindowDates(windowDates, schedStartDate, bidStatus) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    if (
      schedStartDate &&
      (windowDates[0].toMillis() >= schedStartDate.toMillis() || windowDates[1].toMillis() >= schedStartDate.toMillis())
    ) {
      return "Bid Window Dates must be earlier than the Bid Effective Dates";
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }
  return "";
}

export function validateBidName(bidName) {
  if (hasSpecialCharacters(bidName)) {
    // eslint-disable-next-line
    return `Special characters \/ \\ ? % * : | " < > are not allowed`;
  }

  if (bidName.length > 100) {
    return "Bid Name cannot be longer than 100 characters";
  }
  return "";
}

export function validateBidWindowTime(windowDates, hour, minute, station, bidStatus) {
  if (windowDates[0] || windowDates[1]) {
    if (hour.trim() === "" || minute.trim() === "") {
      return "Bid Window Time is required";
    }

    const now = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
    const windowStartTime = windowDates[0].set({ hour: hour, minute: minute, second: 0, millisecond: 0 });
    const windowEndTime = windowDates[1].set({ hour: hour, minute: minute, second: 0, millisecond: 0 });
    if (isPendingBid(bidStatus) && windowStartTime.toMillis() < now.toMillis()) {
      return "Bid Window cannot start in the past";
    }
    if ((isOpenBid(bidStatus) || isPendingBid(bidStatus)) && windowEndTime.toMillis() < now.toMillis()) {
      return "Bid Window cannot end in the past";
    }
  }
  return "";
}

export function validateEmployeeDataForBidGroup(input, bidgroup) {
  if (input === null) {
    return "Not loaded";
  }
  if (!input.isActive) {
    return INVALID_EMPLOYEE_ID;
  }
  if (bidgroup?.find((item) => item.eid === input.eid)) {
    return "Employee is already in the Bid Group";
  }
  return "";
}

const validateFTPTColumn = (bidLine) => {
  // Must be FT or PT (uppercase)
  return ["FT", "PT"].includes(bidLine["FTorPT"].toUpperCase());
};

const validatePremiumColumn = (bidLine) => {
  // Can be blank, Must be a number
  return !isNaN(bidLine["Premium"]);
};

const validateShiftColumn = (bidLine) => {
  const validTimeRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]-(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;

  // Shift has to be either OFF or hh:mm-hh:mm format.
  return (
    (bidLine["SundayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["SundayShift"])) &&
    (bidLine["MondayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["MondayShift"])) &&
    (bidLine["TuesdayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["TuesdayShift"])) &&
    (bidLine["WednesdayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["WednesdayShift"])) &&
    (bidLine["ThursdayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["ThursdayShift"])) &&
    (bidLine["FridayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["FridayShift"])) &&
    (bidLine["SaturdayShift"].trim().toUpperCase() === "OFF" || validTimeRegex.test(bidLine["SaturdayShift"]))
  );
};

const validatePositionColumn = (bidLine, contract, positions) => {
  // If Shift day is OFF, then position must be blank.
  // If IAM or TWU, then position must be in the list of positions.
  // If Non-Contract, then position can be anything but a number.
  positions = positions.map((position) => position.toUpperCase());

  //Shifts values are OFF
  const isSundayShiftOff = bidLine["SundayShift"].trim().toUpperCase() === "OFF";
  const isMondayShiftOff = bidLine["MondayShift"].trim().toUpperCase() === "OFF";
  const isTuesdayShiftOff = bidLine["TuesdayShift"].trim().toUpperCase() === "OFF";
  const isWednesdayShiftOff = bidLine["WednesdayShift"].trim().toUpperCase() === "OFF";
  const isThursdayShiftOff = bidLine["ThursdayShift"].trim().toUpperCase() === "OFF";
  const isFridayShiftOff = bidLine["FridayShift"].trim().toUpperCase() === "OFF";
  const isSaturdayShiftOff = bidLine["SaturdayShift"].trim().toUpperCase() === "OFF";

  //Shift position values
  const sundayPosition = bidLine["SundayPosition"].toUpperCase();
  const mondayPosition = bidLine["MondayPosition"].toUpperCase();
  const tuesdayPosition = bidLine["TuesdayPosition"].toUpperCase();
  const wednesdayPosition = bidLine["WednesdayPosition"].toUpperCase();
  const thursdayPosition = bidLine["ThursdayPosition"].toUpperCase();
  const fridayPosition = bidLine["FridayPosition"].toUpperCase();
  const saturdayPosition = bidLine["SaturdayPosition"].toUpperCase();

  if (contract === NON_CONTRACT)
    return (
      (isSundayShiftOff ? sundayPosition.trim() === "" : isNaN(sundayPosition)) &&
      (isMondayShiftOff ? mondayPosition.trim() === "" : isNaN(mondayPosition)) &&
      (isTuesdayShiftOff ? tuesdayPosition.trim() === "" : isNaN(tuesdayPosition)) &&
      (isWednesdayShiftOff ? wednesdayPosition.trim() === "" : isNaN(wednesdayPosition)) &&
      (isThursdayShiftOff ? thursdayPosition.trim() === "" : isNaN(thursdayPosition)) &&
      (isFridayShiftOff ? fridayPosition.trim() === "" : isNaN(fridayPosition)) &&
      (isSaturdayShiftOff ? saturdayPosition.trim() === "" : isNaN(saturdayPosition))
    );
  else
    return (
      (isSundayShiftOff ? sundayPosition.trim() === "" : positions.includes(sundayPosition.trim())) &&
      (isMondayShiftOff ? mondayPosition.trim() === "" : positions.includes(mondayPosition.trim())) &&
      (isTuesdayShiftOff ? tuesdayPosition.trim() === "" : positions.includes(tuesdayPosition.trim())) &&
      (isWednesdayShiftOff ? wednesdayPosition.trim() === "" : positions.includes(wednesdayPosition.trim())) &&
      (isThursdayShiftOff ? thursdayPosition.trim() === "" : positions.includes(thursdayPosition.trim())) &&
      (isFridayShiftOff ? fridayPosition.trim() === "" : positions.includes(fridayPosition.trim())) &&
      (isSaturdayShiftOff ? saturdayPosition.trim() === "" : positions.includes(saturdayPosition.trim()))
    );
};

const validateAssignmentColumn = (bidLine) => {
  // Can be blank, Must not be a number
  return (
    (bidLine["SundayAssignment"].trim() === "" || isNaN(bidLine["SundayAssignment"])) &&
    (bidLine["MondayAssignment"].trim() === "" || isNaN(bidLine["MondayAssignment"])) &&
    (bidLine["TuesdayAssignment"].trim() === "" || isNaN(bidLine["TuesdayAssignment"])) &&
    (bidLine["WednesdayAssignment"].trim() === "" || isNaN(bidLine["WednesdayAssignment"])) &&
    (bidLine["ThursdayAssignment"].trim() === "" || isNaN(bidLine["ThursdayAssignment"])) &&
    (bidLine["FridayAssignment"].trim() === "" || isNaN(bidLine["FridayAssignment"])) &&
    (bidLine["SaturdayAssignment"].trim() === "" || isNaN(bidLine["SaturdayAssignment"]))
  );
};

// Relief bidline is not supposed to have Sat and/or Sun Off.
const validateReliefBidLineOffDays = (bidLine) => {
  //Shift position values
  const sundayPosition = bidLine["SundayPosition"].trim().toUpperCase();
  const mondayPosition = bidLine["MondayPosition"].trim().toUpperCase();
  const tuesdayPosition = bidLine["TuesdayPosition"].trim().toUpperCase();
  const wednesdayPosition = bidLine["WednesdayPosition"].trim().toUpperCase();
  const thursdayPosition = bidLine["ThursdayPosition"].trim().toUpperCase();
  const fridayPosition = bidLine["FridayPosition"].trim().toUpperCase();
  const saturdayPosition = bidLine["SaturdayPosition"].trim().toUpperCase();

  const isReliefBidLine = [
    sundayPosition,
    mondayPosition,
    tuesdayPosition,
    wednesdayPosition,
    thursdayPosition,
    fridayPosition,
    saturdayPosition,
  ].includes("RELIEF");

  // Sun, Sat Off
  const isWeekendOff =
    bidLine["SundayShift"].trim().toUpperCase() === "OFF" || bidLine["SaturdayShift"].trim().toUpperCase() === "OFF";

  return !(isReliefBidLine && isWeekendOff);
};

export const validateBidLines = (bidLine, contract, positions) => {
  // Validate all columns
  return (
    validateFTPTColumn(bidLine) &&
    validatePremiumColumn(bidLine) &&
    validateShiftColumn(bidLine) &&
    validatePositionColumn(bidLine, contract, positions) &&
    validateAssignmentColumn(bidLine) &&
    validateReliefBidLineOffDays(bidLine)
  );
};

export function validateFTOCount(input, noOfChoices) {
  if (input === "") {
    return "FTO count is required";
  }

  if (!/^(10|[0-9])$/.test(input)) {
    return "Invalid FTO count"; // Invalid input
  }

  // # of ftoCount is more than 0, but 0 choices are selected
  if (parseInt(input) > 0 && noOfChoices === 0) {
    return "At least 1 FTO choice is required";
  }

  // # of ftoCount is 0, but more than 0 choices are selected
  if (noOfChoices > 0 && parseInt(input) === 0) {
    return "At least 1 FTO is required to enter choices";
  }

  return "";
}

export function validateDATCount(input, noOfChoices) {
  if (input === "") {
    return "DAT count is required";
  }

  if (!/^(2[0-5]|1\d|\d)$/.test(input)) {
    return "Invalid DAT count"; // Invalid input
  }

  // # of datCount is more than 0, but 0 choices are selected
  if (parseInt(input) > 0 && noOfChoices === 0) {
    return "At least 1 DAT choice is required";
  }

  // # of datCount is 0, but more than 0 choices are selected
  if (noOfChoices > 0 && parseInt(input) === 0) {
    return "At least 1 DAT is required to enter choices";
  }

  return "";
}

export function validatePTOCount(input, noOfChoices) {
  if (input === "") {
    return "PTO count is required";
  }

  if (!/^(3[0-1]|2\d|1\d|\d)$/.test(input)) {
    return "Invalid PTO count"; // Invalid input
  }

  // # of ptoCount is more than 0, but 0 choices are selected
  if (parseInt(input) > 0 && noOfChoices === 0) {
    return "At least 1 PTO choice is required";
  }

  // # of ptoCount is 0, but more than 0 choices are selected
  if (noOfChoices > 0 && parseInt(input) === 0) {
    return "At least 1 PTO is required to enter choices";
  }

  return "";
}

export function validateGOCHOLCount(input, noOfChoices) {
  if (input === "") {
    return "GOC-HOL count is required";
  }

  if (!/^([0-8])$/.test(input)) {
    return "Invalid GOC-HOL count"; // Invalid input
  }

  // # of GOCHOLCount is more than 0, but 0 choices are selected
  if (parseInt(input) > 0 && noOfChoices === 0) {
    return "At least 1 GOC-HOL choice is required";
  }

  // # of GOCHOLCount is 0, but more than 0 choices are selected
  if (noOfChoices > 0 && parseInt(input) === 0) {
    return "At least 1 GOC-HOL is required to enter choices";
  }

  return "";
}

export function validateAllotmentValue(input, calcDailyAllotments = 1) {
  if (input === "") {
    return "Allotment value is required";
  }

  const numberInput = Number(input);
  if (isNaN(numberInput)) {
    return "Invalid value";
  }

  if (numberInput < calcDailyAllotments) {
    return "Cannot go below calculated daily allotments";
  }

  return "";
}

export function validateNonContractBidWindowDates(windowDates, schedMonth, schedYear, bidStatus) {
  if (windowDates[0] && windowDates[1]) {
    const daysDiff = windowDates[1].diff(windowDates[0], ["days"]).values.days;

    const effectiveDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
    const firstDayOfGivenMonth = getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_NON_CONTRACT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    );

    const lastDayOfPrevMonth = effectiveDate.minus({ months: 1 }).endOf("month");
    if (
      schedMonth &&
      schedYear &&
      (windowDates[0].toMillis() < firstDayOfGivenMonth.toMillis() ||
        windowDates[1].toMillis() > lastDayOfPrevMonth.toMillis())
    ) {
      return `Bid Open and Close Dates must be within ${process.env.REACT_APP_NON_CONTRACT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH} months of the Bid Effective month`;
    }

    if (isPendingBid(bidStatus) && (daysDiff < 1 || daysDiff > 7)) {
      return "Bid Window must be between 1 and 7 days";
    }

    if (isOpenBid(bidStatus) && daysDiff < 1) {
      return "Bid Window must be greater than 1 day";
    }
  }

  return "";
}
